import { Link } from "react-router-dom";
import { Facebook, Instagram, Mail, Youtube } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { TikTokIcon } from "./icons";

const Footer = () => {
  const { t, lang } = useLanguage();
  const socials = [
    { name: "YouTube", Icon: Youtube, id: "social-youtube", href: "https://www.youtube.com/@lingomind-v01" },
    { name: "Instagram", Icon: Instagram, id: "social-instagram", href: "https://www.instagram.com/lingomindapp/" },
    { name: "Facebook", Icon: Facebook, id: "social-facebook", href: "https://www.facebook.com/profile.php?id=61593085982319" },
    { name: "TikTok", Icon: TikTokIcon, id: "social-tiktok", href: "https://www.tiktok.com/@lingomindapp" },
  ];

  return (
    <footer className="border-t border-sky-100 bg-white" data-testid="footer">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-8">
        <div className="grid gap-12 md:grid-cols-[1.4fr_1fr_1fr_1fr]">
          <div>
            <div className="flex items-center gap-2.5">
              <img src="/logo-app.png" alt="LingoMind" className="h-10 w-10 rounded-xl" />
              <span className="font-heading text-xl font-bold text-slate-900">
                Lingo<span className="text-sky-600">Mind</span>
                <span className="text-amber-500">.</span>
              </span>
            </div>
            <p className="mt-4 max-w-xs text-sm leading-relaxed text-slate-600">{t.footer.tagline}</p>
            <a
              data-testid="footer-support-email"
              href="mailto:support@lingomind.app"
              className="mt-5 inline-flex items-center gap-2 rounded-full border border-sky-200 px-4 py-2 text-sm font-semibold text-sky-700 transition-colors hover:border-amber-400 hover:text-amber-600"
            >
              <Mail className="h-4 w-4" />
              support@lingomind.app
            </a>
          </div>

          <div>
            <h4 className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-slate-400">
              {t.footer.product}
            </h4>
            <ul className="mt-4 space-y-2.5 text-sm font-medium text-slate-600">
              <li><a data-testid="footer-link-features" href={`/${lang}#features`} className="transition-colors hover:text-sky-700">{t.nav.features}</a></li>
              <li><a data-testid="footer-link-videos" href={`/${lang}#videos`} className="transition-colors hover:text-sky-700">{t.nav.videos}</a></li>
              <li><a data-testid="footer-link-languages" href={`/${lang}#languages`} className="transition-colors hover:text-sky-700">{t.nav.languages}</a></li>
            </ul>
          </div>

          <div>
            <h4 className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-slate-400">
              {t.footer.resources}
            </h4>
            <ul className="mt-4 space-y-2.5 text-sm font-medium text-slate-600">
              <li><Link data-testid="footer-link-help" to={`/${lang}/help`} className="transition-colors hover:text-sky-700">{t.footer.helpCenter}</Link></li>
              <li><Link data-testid="footer-link-guide" to={`/${lang}/help`} className="transition-colors hover:text-sky-700">{t.footer.userGuide}</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-slate-400">
              {t.footer.connect}
            </h4>
            <div className="mt-4 flex gap-3">
              {socials.map(({ name, Icon, id, href }) => (
                <a
                  key={name}
                  data-testid={id}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={name}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 text-slate-400 transition-all hover:-translate-y-0.5 hover:border-amber-400 hover:bg-amber-50 hover:text-amber-600 hover:shadow-lg hover:shadow-amber-500/15"
                >
                  <Icon className="h-4.5 w-4.5" />
                  <span className="sr-only">{name}</span>
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-14 flex flex-col items-center justify-between gap-3 border-t border-sky-50 pt-8 text-xs text-slate-400 sm:flex-row">
          <p>© {new Date().getFullYear()} LingoMind — www.lingomind.app. {t.footer.rights}</p>
          <p className="font-mono tracking-wider">36 LANGUAGES · 100% OFFLINE · LIFETIME</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
